import InputAdornment from '@mui/material/InputAdornment';
import { TextField } from '@openx/components-core/lib/TextField/TextField.tsx';
import type { RadiusCircle } from '@openx/types/targeting/geographic.ts';
import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkAddDrawer } from '../../shared/BulkAdd/BulkAddDrawer.tsx';
import { useRadius } from './hooks/useRadius.ts';
import { BULK_CIRCLES_SPLITER, LOCATION_LIMIT, MAX_RADIUS, MIN_RADIUS } from './utils/constants.ts';
import { splitCoordinates } from './utils/splitCoordinates.ts';
import { validateBulkCircles } from './utils/validateBulkCircles.ts';

type BulkAddCirclesProps = {
  onClose: () => void;
  onBulkAdd: (items: RadiusCircle[]) => void;
  currentItems: RadiusCircle[];
};

export const BulkAddCircles = ({ onClose, onBulkAdd, currentItems }: BulkAddCirclesProps) => {
  const { t } = useTranslation();
  const { radius, onRadiusChange, radiusError, validateRadius, setRadiusError } = useRadius();

  const itemsLimit = LOCATION_LIMIT - currentItems.length;
  const bulkPlaceholder = i18n.t(`Paste up to 10,000 coordinate pairs (latitude, longitude).
Use either one pair per line or separate pairs with a semicolon (;).

Examples:

One per line:
 31.4321, -118.4556
 40.7128, -74.0060

Semicolon-separated:
 31.4321, -118.4556; 40.7128, -74.0060; 48.8566, 2.3522`);

  const validate = useCallback(
    (coordinatesInput: string[]) => {
      const isRadiusError = !validateRadius(radius);
      setRadiusError(
        isRadiusError ? t('Allowed range: {MIN_RADIUS}–{MAX_RADIUS} (3 decimals)', { MIN_RADIUS, MAX_RADIUS }) : null
      );

      return Promise.resolve(validateBulkCircles(t, coordinatesInput, currentItems, radius));
    },
    [t, currentItems, radius, validateRadius, setRadiusError]
  );

  const onApply = useCallback(
    (validCoordinates: string[]) => {
      onBulkAdd(
        validCoordinates.map((coords: string): RadiusCircle => {
          const splitCoords = splitCoordinates(coords);

          return {
            lat: splitCoords[0].toString(),
            lon: splitCoords[1].toString(),
            rad: radius,
          };
        })
      );
    },
    [onBulkAdd, radius]
  );

  return (
    <BulkAddDrawer
      onClose={onClose}
      onApply={onApply}
      placeholder={bulkPlaceholder}
      limit={itemsLimit}
      validate={validate}
      childParams={radius}
      bulkSplitter={BULK_CIRCLES_SPLITER}
      disableValueClearing
      shouldDisableAddButton={radiusError !== null}
    >
      <TextField
        fullWidth
        label={t('Radius')}
        placeholder={t('Radius')}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="start">MI</InputAdornment>,
          },
        }}
        variant={'outlined'}
        onChange={onRadiusChange}
        value={radius}
        helperText={radiusError}
        error={radiusError !== null}
        sx={{
          mt: '1rem',
          '& .MuiInputLabel-outlined': {
            top: '-1rem',
          },
        }}
      />
    </BulkAddDrawer>
  );
};
